import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Calendar, Pin, Sparkles } from "lucide-react";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });

const isToday = (d) => {
  if (!d) return false;
  const today = new Date();
  const date = new Date(d);
  return today.toDateString() === date.toDateString();
};

const isYesterday = (d) => {
  if (!d) return false;
  const date = new Date(d);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
};

const categoryColor = (c) => {
  switch (c) {
    case "Jubiläum": return "bg-[#ff9800]/15 text-[#ffb84d] border-[#ff9800]/30";
    case "News":     return "bg-[#7c4dff]/15 text-[#b39bff] border-[#7c4dff]/30";
    case "Retro":    return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
    case "Glitch":   return "bg-rose-500/10 text-rose-300 border-rose-500/30";
    case "Community":return "bg-sky-500/10 text-sky-300 border-sky-500/30";
    case "Rare":     return "bg-amber-500/10 text-amber-300 border-amber-500/30";
    case "Event":    return "bg-pink-500/10 text-pink-300 border-pink-500/30";
    default:         return "bg-white/5 text-zinc-300 border-white/10";
  }
};

const NewsCard = ({ article }) => {
  const hasHeader = !!article.header_image;
  const commenters = article.recent_commenters || [];
  const commentCount = article.comment_count ?? 0;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current || visible) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { rootMargin: "-50px 0px", threshold: 0.2 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <Link to={`/news/${article.id}`} className="block group" ref={ref} data-testid={`news-card-${article.id}`}>
      <article className={`card-hover relative overflow-hidden rounded-xl border ${article.sticky ? "border-[#ffb84d]/40 ring-1 ring-[#ffb84d]/20" : "border-white/10"} bg-[#0f141b] ${visible ? "is-visible" : ""}`}>
        {/* Sticky / Kategorie - oben links */}
        <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-1.5">
          {article.sticky && (
            <span className="inline-flex items-center gap-1 rounded-md border border-[#ffb84d]/40 bg-[#ffb84d]/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#ffb84d] backdrop-blur" title="Angepinnt">
              <Pin className="h-3 w-3" /> Sticky
            </span>
          )}
          <span className={`inline-flex items-center rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${categoryColor(article.category)}`}>
            {article.category}
          </span>
        </div>
        {isToday(article.date) && (
          <div className="pointer-events-none absolute right-3 -top-1 z-20" data-testid="news-new-today-badge">
            <div className="news-new-badge inline-flex items-center gap-1 rounded-md border border-rose-400/50 bg-gradient-to-br from-rose-500 to-pink-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg shadow-rose-500/40">
              <Sparkles className="h-3 w-3" /> Neu heute
            </div>
          </div>
        )}
        {!isToday(article.date) && isYesterday(article.date) && (
          <div className="pointer-events-none absolute right-3 -top-1 z-20" data-testid="news-yesterday-badge">
            <div className="news-yesterday-badge inline-flex items-center gap-1 rounded-md border border-amber-400/60 bg-gradient-to-br from-amber-400 to-yellow-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1a0f00] shadow-lg shadow-amber-500/40">
              <Sparkles className="h-3 w-3" /> Gestern
            </div>
          </div>
        )}

        {/* Header mit Titel-Overlay */}
        <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#1a1f29] to-[#10141b]">
          {hasHeader ? (
            <img
              src={article.header_image}
              alt=""
              className="hb-pixel-img absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : article.cover ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={article.cover}
                alt=""
                className="hb-pixel-img h-20 w-20 opacity-90"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          ) : null}

          {/* Gradient-Overlay fuer Lesbarkeit */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f141b] via-[#0f141b]/40 to-transparent" />

          {/* Kommentator-Koepfe rechts oben (verschoben wenn NEU-Badge da ist) */}
          {commenters.length > 0 && (
            <div className={`newscard-commenters absolute right-3 flex items-center gap-1.5 ${(isToday(article.date) || isYesterday(article.date)) ? "top-10" : "top-3"}`}>
              {commenters.map((c, i) => (
                <div
                  key={c.habbo_name + i}
                  className="group newscard-commenter-head relative"
                  style={{ animationDelay: `${i * 120}ms`, zIndex: 10 - i }}
                  title={c.habbo_name + (c.project_badge ? ` – ${c.project_badge.project_title}` : "")}
                >
                  {(() => {
                    const glow = c.user_meta?.custom_glow_color || c.project_badge?.color;
                    if (!glow) return null;
                    return (
                      <div
                        aria-hidden
                        className="comment-glow intense always-pulse absolute left-1/2 top-1/2 h-14 w-14 rounded-full pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${glow}ff 0%, ${glow}66 40%, ${glow}00 75%)`, transform: "translate(-50%, -50%)" }}
                      />
                    );
                  })()}
                  <img
                    src={c.avatar}
                    alt={c.habbo_name}
                    className="hb-pixel-img relative h-10 drop-shadow-[0_0_10px_rgba(124,77,255,0.6)]"
                    loading="lazy"
                  />
                  {c.project_badge && (
                    <img
                      src={`https://images.habbo.com/c_images/album1584/${c.project_badge.code}.gif`}
                      alt={c.project_badge.code}
                      data-testid={`floating-badge-${c.habbo_name}`}
                      className="hb-pixel-img absolute -right-1 -top-1 h-4 w-4 rounded-full bg-[#0f141b] ring-1 z-20"
                      style={{ ['--tw-ring-color']: c.user_meta?.custom_glow_color || c.project_badge.color, boxShadow: `0 0 4px ${c.user_meta?.custom_glow_color || c.project_badge.color}` }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Titel unten ueber Bild */}
          <h3 className="absolute inset-x-3 bottom-3 text-lg font-bold leading-tight text-white drop-shadow-md line-clamp-2 group-hover:text-[#d4c8ff] transition-colors">
            {article.title}
          </h3>
        </div>

        <div className="p-4">
          {/* Verknuepfte Badges (Event/Rare) – als Pixel-Icons unter dem Header */}
          {Array.isArray(article.badges) && article.badges.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid={`news-card-badges-${article.id}`}>
              {article.badges.slice(0, 6).map((b, i) => (
                <img
                  key={(b.code || "") + i}
                  src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`}
                  alt={b.code}
                  title={b.name || b.code}
                  className="hb-pixel-img h-8 w-8 rounded-md bg-[#10141b] ring-1 ring-white/10 p-0.5"
                  loading="lazy"
                />
              ))}
              {article.badges.length > 6 && (
                <span className="ml-1 text-[10px] font-semibold text-zinc-500">+{article.badges.length - 6}</span>
              )}
            </div>
          )}
          <p className="line-clamp-2 text-sm text-zinc-400">{article.excerpt}</p>
          <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> {formatDate(article.date)}{article.time ? ` · ${article.time}` : ""}
            </span>
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3.5 w-3.5" />
              {commentCount} {commentCount === 1 ? "Kommentar" : "Kommentare"}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default NewsCard;
